/**
 * Canonical registry of pre-GA "early adopter" feature flags.
 *
 * A flag is data in Firestore (`featureFlags/{uid}.enabled[key] === true`), read
 * at runtime via the `hasFlag(key)` getter. This list is the single source of
 * which flag keys exist: the main app gates features on these keys, and the
 * admin app (`TheQList/admin-panel`) renders one checkbox per entry. Keep the
 * two copies in sync when adding/removing a flag.
 *
 * Discipline: flags gate FRONTEND exposure only — they are not a security
 * boundary, and pre-GA features behind them must be ADDITIVE (new fields/
 * collections, never repurposing fields GA reads). See docs/PREGA_DATA_RULES.md.
 */
const FEATURE_FLAGS = [
    // Empty since Page Mirror (pageFollow) graduated for the Sun 18 Oct 2026
    // release. Every graduated key stays behind in members' enabled maps as an
    // orphan; hasAnyLiveFlag() intersects against this list, so the badge hides.
];

/**
 * Does this member currently have anything to preview?
 *
 * ⛔ INTERSECTS `enabled` AGAINST THE CURRENT REGISTRY. It iterates
 * FEATURE_FLAGS and asks whether each LIVE key is enabled -- it never asks
 * "does this map contain any true value?".
 *
 * That distinction is the whole point. Retiring a flag (step 44) deletes it
 * from this registry and from the code, but it does NOT clear the key from each
 * member's `featureFlags/{uid}.enabled` map, and nothing else does either. So
 * after Split View graduated on 20 Sept 2026, all 38 member documents still
 * carry `enabled.splitView: true` as an orphan. A naive "any true value" test
 * would be true forever and the badge would never disappear -- which is exactly
 * the outcome this function exists to prevent.
 *
 * Intersecting is also better than clearing the orphan keys on graduation: the
 * registry is already the declared source of truth, and this is self-correcting
 * rather than N writes somebody has to remember.
 *
 * `userPaused` is honoured for the same reason hasFlag() honours it -- a paused
 * member is previewing nothing, whatever their map says.
 *
 * @param {object} ff the `featureFlags/{uid}` document
 */
function hasAnyLiveFlag(ff) {
    if (!ff || ff.userPaused) return false;
    const enabled = ff.enabled;
    if (!enabled) return false;
    return FEATURE_FLAGS.some(flag => enabled[flag.key] === true);
}

export default FEATURE_FLAGS;
export { FEATURE_FLAGS, hasAnyLiveFlag };


// WEBPACK FOOTER //
// ./src/lib/featureFlags.js